import type { AronMessage, AronPendingWrite } from "../types";

/** How many trailing lines of an answer count as "the closing of the message"
 * when looking for a question — a "?" buried in the middle of a long report is
 * not ARON asking the user anything. */
const TAIL_LINES = 3;

/** Trailing markdown/typographic noise that can sit AFTER the question mark. */
const TRAILING_NOISE = /[\s*_`~"'”’)\]>]+$/;

/**
 * Does this assistant message end by asking the user something?
 *
 * Front-end heuristic — the API does not flag "I need an answer" (no schema
 * change, none proposed here). It reads the closing lines of the answer only, so
 * a "?" inside a long report never lights the signal up. It drives a tab badge,
 * nothing else: a false positive costs one glance, never an action.
 */
export function isQuestionToUser(text: string): boolean {
  const lines = (text || "")
    .split("\n")
    .map((l) => l.replace(TRAILING_NOISE, "").trim())
    .filter(Boolean);
  if (lines.length === 0) return false;
  return lines.slice(-TAIL_LINES).some((l) => l.endsWith("?"));
}

/** The last assistant message that has finished (streaming rows don't signal). */
export function lastSettledAssistant(messages: AronMessage[]): AronMessage | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.role !== "assistant") continue;
    if (m.status === "streaming" || m.status === "pending") return null;
    return m;
  }
  return null;
}

/** Write proposals in the whole thread that still wait for approve/reject. */
export function pendingWrites(messages: AronMessage[]): AronPendingWrite[] {
  return messages.flatMap((m) =>
    (m.pending_writes ?? []).filter((w) => w.status === "pending"),
  );
}
